// src/pages/cart/AddToCartPage.tsx
import React, { useState, useEffect } from 'react'
import { useAppSelector, useAppDispatch } from '../../store/hooks'
import {
  selectCartVehicles,
  rentalVehicleSelector,
  setCartEmpty,
  calculateFinalCost
} from '../../redux/rentalSlice/rentalVehicleSlice'
import Header1 from '../../components/header/Header1'
import { fetchCustomerQuotes, requestQuote } from '../../redux/quoteSlice/quoteSlice'
import { authSelector } from '../../redux/authSlice/authSlice'
import { useNavigate } from 'react-router-dom'
import config from '../../config'

interface CartVehicle {
  id: number
  model_name: string
  model_description: string
  vehicle_cat_name: string
  vehicle_subcat_name: string
  rental_cost_per_day: number
  count: number
  image_url: string
  discount_rate: number
  is_demand: number
  available_quantity: number
}

interface Props {
  onClose?: () => void
  isModal?: boolean
}

const AddToCartPage: React.FC<Props> = ({ onClose, isModal = false }) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { rentalDetails, startDate: reduxStartDate, endDate: reduxEndDate, pincode: reduxPincode, startTime: reduxStartTime, endTime: reduxEndTime, eventType: reduxEventType } =
    useAppSelector(rentalVehicleSelector)
  const cartVehicles = useAppSelector(selectCartVehicles).filter(
    (v): v is CartVehicle => v !== null
  )
  // const { userId } = useAppSelector(authSelector)
  const userdetails = localStorage.getItem('user')
  const parsedUser = userdetails ? JSON.parse(userdetails) : null
  const userId = parsedUser[0].userId
  console.log(parsedUser[0].userId, 'uhbhu')

  // Local copies (sync with redux values)
  const [startDate, setStartDate] = useState<string>(reduxStartDate ?? '')
  const [endDate, setEndDate] = useState<string>(reduxEndDate ?? '')
  const [pincode, setPincode] = useState<string>(reduxPincode ?? '')
  const [startTime, setStartTime] = useState<string>(reduxStartTime ?? '')
  const [endTime, setEndTime] = useState<string>(reduxEndTime ?? '')
  const [eventType, setEventType] = useState<string>(reduxEventType ?? '')

  const [pincodeError, setPincodeError] = useState<string>('')
  const [address, setAddress] = useState<string>('')
  const [contactError, setContactError] = useState<string>('')
  const [contact, setContact] = useState<string>('')

  const [city, setCity] = useState('')
  const [area, setArea] = useState('')
  const [driverRequired, setDriverRequired] = useState<boolean>(false)
  const [transportCost, setTransportCost] = useState<number>(0)
  const [loading, setLoading] = useState<boolean>(false)

  const validateContactNumber = (number: string): boolean => {
    if (!number.trim()) {
      setContactError('Contact number is required.')
      return false
    }
    if (!/^\d{10}$/.test(number)) {
      setContactError('Contact number must be a 10-digit number.')
      return false
    }
    setContactError('')
    return true
  }
  // Fetch city & area when pincode changes
  useEffect(() => {
    const fetchCityArea = async () => {
      if (!/^\d{6}$/.test(pincode)) return

      try {
        const res = await fetch(`https://api.postalpincode.in/pincode/${pincode}`)
        const data = await res.json()

        if (data[0]?.Status === 'Success' && data[0].PostOffice?.length > 0) {
          const office = data[0].PostOffice[0]
          // Use Division or Block as city (more accurate than District)
          setCity(office.Division || office.Block || '')
          // Use Name as area
          setArea(office.Name || '')
        }
      } catch (err) {
        console.error('Error fetching city/area:', err)
      }
    }

    void fetchCityArea()
  }, [pincode])

  // When Redux values change, sync local state
  useEffect(() => {
    if (reduxStartDate) setStartDate(reduxStartDate)
    else {
      // keep previous fallback logic if Redux doesn't have it
      const today = new Date()
      const tomorrow = new Date()
      tomorrow.setDate(today.getDate() + 1)
      setStartDate(today.toISOString().split('T')[0])
      setEndDate(tomorrow.toISOString().split('T')[0])
    }
    if (reduxEndDate) setEndDate(reduxEndDate)
  }, [reduxStartDate, reduxEndDate])

  useEffect(() => {
    if (reduxPincode) setPincode(reduxPincode)
  }, [reduxPincode])

  // Calculate transport cost when pincode or rentalDetails change
  useEffect(() => {
    // wrapper to call async function
    const run = async () => {
      if (!rentalDetails) {
        setTransportCost(0)
        return
      }
      if (!pincode) {
        setTransportCost(0)
        return
      }
      if (!/^\d{6}$/.test(pincode)) {
        setPincodeError('Pincode must be a 6-digit number.')
        setTransportCost(0)
        return
      } else {
        setPincodeError('')
      }

      try {
        const deliveryPincode = pincode.trim()
        // base company coords (kept same as before)
        const companyLon = 78.591902
        const companyLat = 17.470949

        // Get user's pincode coordinates via Nominatim
        const userGeoRes = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&postalcode=${deliveryPincode}&countrycodes=in&limit=1`,
          { headers: { 'User-Agent': 'service-app' } }
        )
        const userGeoData = await userGeoRes.json()
        if (!userGeoData.length) throw new Error('User pincode coordinates not found.')
        const userLon = parseFloat(userGeoData[0].lon)
        const userLat = parseFloat(userGeoData[0].lat)

        // route via OSRM
        const routeRes = await fetch(
          `https://router.project-osrm.org/route/v1/driving/${companyLon},${companyLat};${userLon},${userLat}?overview=false`
        )
        const routeData = await routeRes.json()

        if (!routeData.routes || !routeData.routes.length) {
          setTransportCost(0)
          return
        }

        const distanceMeters = routeData.routes[0].distance
        const distanceKm = distanceMeters / 1000

        let totalTransportFee = 0
        const baseKm = rentalDetails.base_km
        const baseKmAmount = rentalDetails.base_km_amount
        const costPerKm = rentalDetails.cost_per_km

        if (distanceKm > baseKm) {
          const extraKm = distanceKm - baseKm
          totalTransportFee = baseKmAmount + extraKm * costPerKm
        } else {
          totalTransportFee = baseKmAmount
        }

        setTransportCost(Math.round(totalTransportFee))
      } catch (err) {
        console.error('Error calculating transport cost:', err)
        setTransportCost(0)
      }
    }

    void run()
  }, [pincode, rentalDetails])

  const getRentalDays = (): number => {
    if (!startDate || !endDate) return 0
    const start = new Date(startDate)
    const end = new Date(endDate)
    const diff = (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)

    return diff >= 0 ? diff + 1 : 0 // inclusive of start date
  }

  
  const rentalDays = getRentalDays()
  const rentalCost = cartVehicles.reduce(
    (acc, v) => acc + calculateFinalCost(v) * v.count * rentalDays,
    0
  )

  const trainedOperatorCost = rentalDetails?.trained_operator_cost || 0
  // Apply 100% discount to the trained operator cost
  const driverCost = trainedOperatorCost
  const totalCost = rentalCost + transportCost

  const handleRequestQuote = async () => {
    if (!address || !contact) {
      alert('Please fill in delivery address and contact number.')
      return
    }

    if (!validateContactNumber(contact)) {
      // The error state is already set by validateContactNumber
      return
    }

    setLoading(true)

    const payload = {
      customerId: userId,
      rentalDetails: {
        startDate,
        endDate,
        startTime,
        endTime,
        rentalDays,
        driverRequired,
        driverCost,
        transportCost,
        rentalCost
      },
      vehicles: cartVehicles.map((v) => ({
        vehicleId: v.id,
        count: v.count
      })),
      totalCost,
      delivery: {
        pincode,
        city,
        area,
        address,
        contact
      },
      eventType
    }

    try {
      await dispatch(requestQuote(payload)).unwrap()
      alert('✅ Quote requested successfully!')

      dispatch(fetchCustomerQuotes(userId))

      // clear cart and close modal if provided
      dispatch(setCartEmpty())

      if (onClose) {
        onClose()
      } else {
        // keep existing behavior when used as standalone page
        navigate('/rentVehicle')
      }
    } catch (err) {
      console.error('Failed to request quote:', err)
      alert('❌ Failed to request quote. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className='  '>
      {/* Do not render page header inside modal */}
      {!isModal && <Header1 title="Cart" to="/" />}

      <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Left: Selected Vehicles */}
        <div>
          <h1 className="text-xl font-bold mb-3">Your Selected Vehicles</h1>

          {cartVehicles.length === 0 ? (
            <p>No vehicles selected.</p>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-4 max-h-64 overflow-y-auto pr-2">
                {cartVehicles.map((v) => (
                  <div
                    key={v.id}
                    className="border rounded-lg p-3 flex flex-col items-center shadow-sm bg-white"
                  >
                    <img
                      src={`${config.imageBaseURL}/${v.image_url}`}
                      alt={v.model_name}
                      className="w-full h-24 object-contain bg-gray-100 rounded mb-2"
                    />
                    <p className="font-semibold text-sm text-center truncate w-full">
                      {v.model_name}
                    </p>
                    <p className="text-xs text-gray-500">Qty: {v.count}</p>
                    <p className="text-xs text-gray-600">
                      ₹{calculateFinalCost(v)}/day
                    </p>
                  </div>
                ))}
              </div>

              {/* Rental Details display (READONLY: values come from RentVehicle via Redux) */}
              <h2 className="text-lg font-bold mt-5 mb-2">Rental Details</h2>
              <div className="flex flex-col gap-2">
                <div className="text-sm">
                  <span className="font-medium">Start:</span>{' '}
                  <span>{startDate || '—'}</span>
                </div>
                <div className="text-sm">
                  <span className="font-medium">End:</span>{' '}
                  <span>{endDate || '—'}</span>
                </div>

                <div className="text-sm">
                  <span className="font-medium">Pincode:</span>{' '}
                  <span>{pincode || '—'}</span>
                </div>
                <div>
                  <label>City:</label>
                  <input
                    type="text"
                    value={city}
                    onChange={(e) => { setCity(e.target.value) }}
                    className="border p-2 w-full rounded"
                    placeholder="City"
                  />
                </div>

                <div>
                  <label>Area:</label>
                  <input
                    type="text"
                    value={area}
                    onChange={(e) => { setArea(e.target.value) }}
                    className="border p-2 w-full rounded"
                    placeholder="Area / Locality"
                  />
                </div>
                <textarea
                  value={address}
                  onChange={(e) => { setAddress(e.target.value) }}
                  className="border p-2 w-full rounded mt-3"
                  placeholder="Delivery Address"
                />
                <input
                  type="tel"
                  value={contact}
                  onChange={(e) => { setContact(e.target.value) }}
                  className="border p-2 w-full rounded"
                  placeholder="Contact Number"
                />
                {contactError && <p className="text-red-500 text-sm mt-1">{contactError}</p>}
              </div>
            </>
          )}
        </div>

        {/* Right: Cost Summary */}
        {!transportCost
          ? 'loading'
          : <div className="bg-gray-50 rounded-lg shadow-md p-6 border border-gray-200">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Order Summary</h2>

          {cartVehicles.length > 0 && (
            <div className="bg-white rounded-lg shadow-inner p-4 mb-4">
              <h3 className="text-lg font-semibold text-gray-700 mb-2">Vehicle Costs</h3>
              <table className="min-w-full text-left text-sm font-light">
                <thead className="border-b font-medium">
                  <tr>
                    <th className="px-3 py-2">Vehicle(Qt)-₹cost/day</th>
                    <th className="px-3 py-2 text-right">Subtotal</th>
                  </tr>
                </thead>
                <tbody>
                  {cartVehicles.map((v) => (
                    <tr key={v.id} className="border-b hover:bg-gray-50">
                      <td className="px-3 py-2 font-medium">
                        {v.model_name} (x{v.count}){' '}
                        <span className="text-xs text-gray-500 italic">
                          — ₹{calculateFinalCost(v)}/day
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right">
                        ₹{(calculateFinalCost(v) * v.count * rentalDays).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="space-y-3">
            <div className="flex justify-between text-gray-600">
              <span>Rental Days</span>
              <span>{rentalDays}</span>
            </div>
            <div className="flex justify-between text-gray-600">
              <span>Rental Cost</span>
              <span>₹{rentalCost.toLocaleString()}</span>
            </div>
            <div className="flex justify-between text-gray-600">
              <span>Transport Cost</span>
              <span>₹{transportCost.toLocaleString()}</span>
            </div>

            <div className="flex justify-between text-gray-600">
              <span>Operator Cost (100% Discounted)</span>
              <span className="line-through text-gray-500">₹{driverCost.toLocaleString()}</span>
            </div>

            <hr />
            <div className="flex justify-between text-xl font-bold">
              <span>Total Amount</span>
              <span className="text-green-600">₹{totalCost.toLocaleString()}</span>
            </div>
          </div>

          <button
            onClick={handleRequestQuote}
            disabled={loading}
            className="mt-6 bg-blue-600 text-white font-semibold px-6 py-3 rounded-xl w-full disabled:opacity-50"
          >
            {loading ? 'Submitting...' : 'Request Quote'}
          </button>
        </div>}
      </div>
    </div>
  )
}

export default AddToCartPage
